import type { MessageKey } from '../../i18n/messages.en.ts';

/**
 * MVP-272 Phase 1: `QueuePositionCard`'s "Asked {age}" line (`guest.queue.askedPrefix`) used to
 * interpolate `lib/format.ts`'s `formatRelativeAge()` output directly — plain English every time,
 * regardless of locale, so a Thai guest saw e.g. "ขอไว้เมื่อ 5 min ago". `formatRelativeAge` itself
 * stays untouched this phase: it is shared with surfaces that are still English (see its own call
 * sites outside `features/guest/`), and translating it here would rewrite copy this phase has no
 * mandate to touch.
 *
 * This module is the guest-only translation of the SAME bucketing `formatRelativeAge` does, kept
 * as a small pure `{ key, params }` result (same idiom as `guestSongRowLabel.ts`/
 * `guestRefetch.ts`'s `describeQueuePosition`) that `QueuePositionCard` resolves via `t()`.
 *
 * DELIBERATE DUPLICATION: the `< 60s` / `< 60 min` / `< 24 h` / `=== 1 day` / `> 1 day` thresholds
 * below are copy-pasted from `formatRelativeAge`, not imported from it, because there is nothing to
 * import that would help — `formatRelativeAge` returns a finished English string, not the bucketing
 * decision on its own. A later phase that translates `lib/format.ts` itself (once every surface
 * using it is translated, not just this one) should collapse these two into one shared bucketing
 * function that both `formatRelativeAge` and this module call, rather than leaving both copies of
 * the threshold logic to drift apart independently.
 */
export interface RequestAgeDescription {
  key: MessageKey;
  params?: Record<string, number>;
}

export function describeRequestAge(ms: number | null | undefined): RequestAgeDescription {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return { key: 'guest.queue.age.unknown' };
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return { key: 'guest.queue.age.justNow' };
  const min = Math.floor(sec / 60);
  if (min < 60) return { key: 'guest.queue.age.minutes', params: { n: min } };
  const hr = Math.floor(min / 60);
  if (hr < 24) return { key: 'guest.queue.age.hours', params: { n: hr } };
  const days = Math.floor(hr / 24);
  if (days === 1) return { key: 'guest.queue.age.yesterday' };
  return { key: 'guest.queue.age.days', params: { n: days } };
}
